import { ArrowDownRight, ArrowUpRight, Gauge, Pause, Play, Thermometer } from "lucide-react";
import { type Interval, type TemperatureUnit } from "@anthemreactv2/roast-domain";
import { formatTime, type Workspace } from "../workspace";
import { plotPath } from "../CurveChart";
import { Button } from "@/components/ui/button";

export function RoastReadouts({ workspace, bean, ror, unit, interval, windowSeconds, playing, speed, onToggle }: {
  workspace: Workspace; bean: number | null; ror: number | null; unit: TemperatureUnit;
  interval: Interval; windowSeconds: Interval; playing: boolean; speed: number; onToggle: () => void;
}) {
  const lastMs = workspace.source.samples[workspace.source.samples.length - 1]!.elapsedMs;
  const progress = workspace.playheadMs / Math.max(lastMs, 1);
  const history = workspace.source.samples.filter(sample => sample.elapsedMs <= workspace.playheadMs && sample.elapsedMs >= workspace.playheadMs - 60000);
  const readings = history.flatMap(sample => sample.beanC === null ? [] : [sample.beanC]);
  const low = readings.length ? Math.min(...readings) : 0;
  const high = readings.length ? Math.max(...readings) : 1;
  const start = Math.max(0, workspace.playheadMs - 60000);
  const trace = plotPath(history.map(sample => ({ elapsedMs: sample.elapsedMs, value: sample.beanC })),
    ms => (ms - start) / Math.max(1, workspace.playheadMs - start) * 156 + 2,
    value => 48 - (value - low) / Math.max(high - low, 1) * 30, 3000);
  const Trend = ror !== null && ror < 0 ? ArrowDownRight : ArrowUpRight;

  return (
    <div className="roast-readouts metrics" aria-label="Roast readings">
      <div className="readout-card session-clock">
        <div className="readout-label"><span>Session clock</span><span className="clock-state">{playing ? `${speed}× replay` : "Paused"}</span></div>
        <div className="clock-body">
          <svg viewBox="0 0 112 112" className="clock-dial" aria-hidden="true">
            {Array.from({ length: 60 }, (_, index) => {
              const angle = index * Math.PI / 30 - Math.PI / 2;
              const inner = index % 5 === 0 ? 39 : 43;
              return <line key={index} x1={56 + Math.cos(angle) * inner} y1={56 + Math.sin(angle) * inner} x2={56 + Math.cos(angle) * 48} y2={56 + Math.sin(angle) * 48} className={index / 60 < progress ? "clock-tick active" : "clock-tick"} />;
            })}
            <circle cx="56" cy="56" r="29" className="clock-center" />
            <path d="M56 35V61" transform={`rotate(${progress * 360} 56 56)`} className="clock-hands" />
            <circle cx="56" cy="56" r="2" className="clock-pivot" />
          </svg>
          <div className="clock-info"><strong>{formatTime(workspace.playheadMs)}</strong><span>of {formatTime(lastMs)} recorded</span>
            <Button className="clock-play" disabled={!playing && workspace.playheadMs >= lastMs} onClick={onToggle}>{playing ? <Pause /> : <Play />}{playing ? "Pause replay" : "Play replay"}</Button>
          </div>
        </div>
      </div>
      <div className="readout-card bean-readout">
        <div className="readout-label"><span>Bean temperature</span><Thermometer aria-hidden="true" size={16} /></div>
        <div className="readout-value"><strong>{bean === null ? "—" : bean.toFixed(1)}</strong><span>°{unit}</span></div>
        <div className="readout-bottom"><span>{bean === null ? "No current reading" : "Recorded temperature"}<small>Last 60 seconds</small></span>
          <svg viewBox="0 0 160 60" className="readout-spark" role="img" aria-label="Recorded bean temperature over the last minute"><path d={trace} /></svg>
        </div>
      </div>
      <div className="readout-card ror-readout">
        <div className="readout-label"><span>Rate of Rise</span><Gauge aria-hidden="true" size={16} /></div>
        <div className="readout-value"><strong>{ror === null ? "—" : ror.toFixed(1)}</strong><span>°{unit} / {interval}s</span></div>
        <div className="readout-bottom"><span>{ror === null ? "Waiting for continuous history" : `${windowSeconds}s trailing calculation`}<small>Display interval: {interval} seconds</small></span><span className="trend-mark" aria-hidden="true">{ror === null ? "—" : <Trend size={26} strokeWidth={1.5} />}</span></div>
      </div>
    </div>
  );
}
